import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostBinding, Input, OnDestroy,
  OnInit, Output, ViewEncapsulation
} from '@angular/core';

import { Subscription } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';

import { DeviceService, PositionEnum, SdBannerComponent, SdButtonComponent, SdTitleComponent } from '@seb/web-design-system/core';

import type { ITile } from './tile.interface';

@Component({
  selector: 'sd-tile',
  templateUrl: './tile.component.html',
  styleUrls: ['./tile.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    SdTitleComponent,
    SdBannerComponent,
    SdButtonComponent,
    TranslateModule
  ]
})
export class SdTileComponent implements OnInit, OnDestroy {
  constructor(
    private el: ElementRef,
    private deviceService: DeviceService,
    private cdRef: ChangeDetectorRef,
  ) {}

  isMobile$: boolean;
  isImage: boolean;
  isVideo: boolean;
  isSlider: boolean;
  mediaConfigClass: string;
  mobileSubscription: Subscription;
  _mediaConfig:
    | PositionEnum.bottomLeft
    | PositionEnum.bottomRight
    | PositionEnum.topLeft
    | PositionEnum.topRight = PositionEnum.bottomRight;
  contentClass: string;

  @Input()
  tile: ITile;

  @Input()
  tilePosition: PositionEnum.left | PositionEnum.right = PositionEnum.right;

  /**
   * Allow to define the level of the title tag (h1 - h6).
   * * @param {number} 1 to 6
   */
  @Input()
  titleLevel = 3;

  /**
   * Allow to define if the text is aligned to the left or the right, or even centered
   * * @param {string} bottom-left | bottom-right | top-left | top-right
   */
  @Input()
  get mediaConfig() {
    return this._mediaConfig;
  }
  set mediaConfig(
    mediaConfig:
      | PositionEnum.bottomLeft
      | PositionEnum.bottomRight
      | PositionEnum.topLeft
      | PositionEnum.topRight,
  ) {
    if (!mediaConfig) {
      throw new Error(
        `The config you're giving is ${mediaConfig}. \n
        Please use one of the following 'top-left', 'top-right', 'bottom-left', 'bottom-right'" \n
        See types: ${PositionEnum.bottomLeft} | ${PositionEnum.bottomRight} | ${PositionEnum.topLeft} | ${PositionEnum.topRight}
      `,
      );
    }
    this._mediaConfig = mediaConfig;
    this.contentClass = 'is-' + this._mediaConfig;
  }

  @Input()
  twicPicsAutoSize = true;

  @Output()
  openModal = new EventEmitter<{ state: boolean; target: ElementRef }>();

  @Input() class = ''; // override the standard class attr with a new one.
  @HostBinding('class')
  get hostClasses(): string {
    return [this.class, 'is-' + this.tilePosition].filter(Boolean).join(' ');
  }

  ngOnInit() {
    this.mediaConfigClass = 'is-' + this.mediaConfig;

    this.mobileSubscription = this.deviceService.isMobile.subscribe((x) => {
      this.isMobile$ = x;
      this.cdRef.markForCheck();
    });
  }

  ngOnDestroy() {
    this.mobileSubscription?.unsubscribe();
  }

  onOpenModal() {
    this.openModal.emit({
      state: true,
      target: this.el.nativeElement.querySelector('button')
    });
  }
}
